// src/styles/global.stylex.ts
import * as stylex from '@stylexjs/stylex';
import { darkTokens } from './tokens.stylex';

/**
 * Global base styles that will be applied to the root elements.
 * These replace the global CSS rules from globals.css.
 */

export const globalStyles = stylex.create({
    // Universal reset
    reset: {
        boxSizing: 'border-box',
        margin: 0,
        padding: 0,
    },

    // HTML element
    html: {
        scrollBehavior: 'smooth',
    },

    // Body element
    body: {
        margin: 0,
        padding: 0,
        fontFamily: darkTokens.fontFamily,
        fontSize: darkTokens.fontMd,
        lineHeight: 1.6,
        color: darkTokens.textPrimary,
        backgroundColor: darkTokens.bg,
        transition: `background-color ${darkTokens.transitionNormal}, color ${darkTokens.transitionNormal}`,
    },
});

/**
 * Base styles and scrollbar styles (injected as raw CSS since they target global elements)
 * These will be injected separately in main.tsx
 */
export const baseStylesCSS = `
/* Universal reset */
* {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
}

/* HTML element */
html {
    scroll-behavior: smooth;
}

/* Body element */
body {
    margin: 0;
    padding: 0;
    font-family: var(--font-family);
    font-size: var(--font-size-md);
    line-height: 1.6;
    color: var(--text-primary);
    background-color: var(--bg);
    transition: background-color var(--transition-normal), color var(--transition-normal);
}

/* Typography */
h1, h2, h3, h4, h5, h6 {
    margin: 0 0 var(--spacing-md) 0;
    font-weight: 600;
    line-height: 1.25;
    color: var(--text-primary);
}

h1 { font-size: var(--font-size-3xl); }
h2 { font-size: var(--font-size-2xl); }
h3 { font-size: var(--font-size-xl); }
h4 { font-size: var(--font-size-lg); }
h5 { font-size: var(--font-size-md); }
h6 { font-size: var(--font-size-sm); }

p {
    margin: 0 0 var(--spacing-md) 0;
    color: var(--text-secondary);
}

a {
    color: var(--primary);
    text-decoration: none;
    transition: color var(--transition-normal);
}

a:hover {
    color: var(--primary-hover);
}

/* Scrollbar Styling */
* {
    scrollbar-width: thin;
    scrollbar-color: var(--scrollbar-thumb-color) var(--scrollbar-track-color);
}

*::-webkit-scrollbar {
    width: var(--scrollbar-width);
    height: var(--scrollbar-width);
}

*::-webkit-scrollbar-track {
    background: var(--scrollbar-track-color);
}

*::-webkit-scrollbar-thumb {
    background-color: var(--scrollbar-thumb-color);
    border-radius: var(--radius-full);
}

*::-webkit-scrollbar-thumb:hover {
    background-color: var(--scrollbar-thumb-hover-color);
}
`;

/**
 * CSS Variables for theme switching
 * These need to be injected as CSS to work with the existing theme toggle
 * and maintain compatibility with existing inline styles using var(--)
 */
export const themeVariablesCSS = `
:root {
    /* Base Color Palette - HSL values for easy manipulation */
    --hue-primary: 210;
    --hue-secondary: 264;
    --hue-accent: 330;
    --hue-success: 120;
    --hue-warning: 45;
    --hue-error: 355;
    --hue-neutral: 210;

    /* Dark Mode (Default) Theme */
    --accent: hsl(var(--hue-accent), 90%, 60%);
    --accent-light: hsl(var(--hue-accent), 85%, 40%);
    --accent-hover: hsl(var(--hue-accent), 95%, 70%);

    --primary: hsl(var(--hue-primary), 85%, 60%);
    --primary-light: hsl(var(--hue-primary), 85%, 40%);
    --primary-hover: hsl(var(--hue-primary), 85%, 70%);

    --secondary: hsl(var(--hue-secondary), 70%, 60%);
    --secondary-light: hsl(var(--hue-secondary), 70%, 40%);
    --secondary-hover: hsl(var(--hue-secondary), 70%, 70%);

    --success: hsl(var(--hue-success), 60%, 50%);
    --success-hover: hsl(var(--hue-success), 60%, 60%);

    --warning: hsl(var(--hue-warning), 90%, 60%);
    --warning-hover: hsl(var(--hue-warning), 90%, 70%);

    --error: hsl(var(--hue-error), 85%, 55%);
    --error-hover: hsl(var(--hue-error), 85%, 65%);

    /* Background & Surface Colors */
    --bg: hsl(var(--hue-neutral), 30%, 12%);
    --bg-hover: hsl(var(--hue-neutral), 30%, 17%);
    --bg-secondary: hsl(var(--hue-neutral), 30%, 18%);
    --bg-surface: var(--bg-card);
    --bg-card: hsl(var(--hue-neutral), 25%, 15%);

    /* Text Colors */
    --text-primary: hsl(var(--hue-neutral), 15%, 90%);
    --text-secondary: hsl(var(--hue-neutral), 15%, 75%);
    --text-tertiary: hsl(var(--hue-neutral), 15%, 60%);
    --text-on-accent: hsl(0, 0%, 100%);

    /* Border Colors */
    --border: hsl(var(--hue-neutral), 20%, 25%);
    --border-hover: hsl(var(--hue-neutral), 20%, 35%);

    /* Focus/Selection */
    --focus-ring: hsla(var(--hue-primary), 90%, 60%, 0.5);
    --selection-bg: hsla(var(--hue-primary), 90%, 40%, 0.5);

    /* Primary variations with opacity */
    --primary-bg-subtle: hsla(var(--hue-primary), 85%, 60%, 0.1);
    --primary-bg-light: hsla(var(--hue-primary), 85%, 60%, 0.15);
    --primary-bg-medium: hsla(var(--hue-primary), 85%, 60%, 0.3);
    --primary-border-light: hsla(var(--hue-primary), 85%, 60%, 0.3);

    /* Additional color variations with opacity */
    --success-bg-subtle: hsla(var(--hue-success), 60%, 50%, 0.1);
    --accent-bg-subtle: hsla(var(--hue-accent), 90%, 60%, 0.1);
    --warning-bg-subtle: hsla(var(--hue-warning), 90%, 60%, 0.1);
    --secondary-bg-subtle: hsla(var(--hue-secondary), 70%, 60%, 0.1);

    /* Overlay colors */
    --overlay-bg: rgba(0, 0, 0, 0.6);

    /* Typography */
    --font-family: 'Poppins', sans-serif;
    --header-height: 64px;

    /* Transitions */
    --transition-fast: 0.2s;
    --transition-normal: 0.3s;
    --transition-slow: 0.5s;

    /* Border Radius */
    --radius-sm: 4px;
    --radius-md: 8px;
    --radius-lg: 12px;
    --radius-xl: 20px;
    --radius-full: 9999px;

    /* Shadows */
    --shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
    --shadow-md: 0 4px 6px rgba(0, 0, 0, 0.1);
    --shadow-lg: 0 10px 15px rgba(0, 0, 0, 0.1);
    --shadow-xl: 0 20px 25px rgba(0, 0, 0, 0.1);

    /* Spacing */
    --spacing-xs: 0.25rem;
    --spacing-sm: 0.5rem;
    --spacing-md: 1rem;
    --spacing-lg: 1.5rem;
    --spacing-xl: 2rem;
    --spacing-2xl: 3rem;
    --spacing-3xl: 4rem;

    /* Font Sizes */
    --font-size-xs: 0.75rem;
    --font-size-sm: 0.875rem;
    --font-size-md: 1rem;
    --font-size-lg: 1.25rem;
    --font-size-xl: 1.5rem;
    --font-size-2xl: 2rem;
    --font-size-3xl: 2.5rem;

    /* Layout */
    --sidebar-width: 280px;
    --max-width: 1200px;

    /* Z-index */
    --z-index-modal: 1000;
    --z-index-overlay: 900;
    --z-index-dropdown: 800;
    --z-index-header: 700;
    --z-index-tooltip: 600;

    /* Scrollbar */
    --scrollbar-width: 8px;
    --scrollbar-track-color: transparent;
    --scrollbar-thumb-color: hsla(var(--hue-primary), 85%, 60%, 0.4);
    --scrollbar-thumb-hover-color: hsla(var(--hue-primary), 85%, 70%, 0.7);

    /* Glass Morphism Variables - For popup components */
    --glass-bg-very-subtle: rgba(255, 255, 255, 0.02);
    --glass-bg-subtle: rgba(255, 255, 255, 0.05);
    --glass-bg-light: rgba(255, 255, 255, 0.08);
    --glass-bg-medium: rgba(255, 255, 255, 0.12);
    --glass-bg-strong: rgba(255, 255, 255, 0.18);
    --glass-border-light: rgba(255, 255, 255, 0.1);
    --glass-border-medium: rgba(255, 255, 255, 0.15);
    --glass-border-strong: rgba(255, 255, 255, 0.25);

    /* Glass Overlays */
    --overlay-light: rgba(255, 255, 255, 0.05);
    --overlay-medium: rgba(255, 255, 255, 0.1);
    --overlay-subtle: rgba(255, 255, 255, 0.02);

    /* Glass Shadows */
    --shadow-focus-primary: 0 0 0 3px rgba(139, 92, 246, 0.3);
    --shadow-focus-error: 0 0 0 3px rgba(239, 68, 68, 0.3);
    --shadow-glass: 0 8px 32px rgba(0, 0, 0, 0.3);
    --shadow-glass-hover: 0 12px 40px rgba(0, 0, 0, 0.4);
    --shadow-glow-primary: 0 0 20px rgba(139, 92, 246, 0.4);

    /* Shimmer effects */
    --shimmer-light: rgba(255, 255, 255, 0.1);
}

/* Light Mode Theme */
.light {
    --accent: hsl(var(--hue-accent), 85%, 50%);
    --accent-light: hsl(var(--hue-accent), 85%, 70%);
    --accent-hover: hsl(var(--hue-accent), 85%, 40%);

    --primary: hsl(var(--hue-primary), 85%, 45%);
    --primary-light: hsl(var(--hue-primary), 85%, 65%);
    --primary-hover: hsl(var(--hue-primary), 85%, 35%);

    --secondary: hsl(var(--hue-secondary), 65%, 50%);
    --secondary-light: hsl(var(--hue-secondary), 65%, 70%);
    --secondary-hover: hsl(var(--hue-secondary), 65%, 40%);

    --success: hsl(var(--hue-success), 65%, 45%);
    --success-hover: hsl(var(--hue-success), 65%, 35%);

    --warning: hsl(var(--hue-warning), 90%, 55%);
    --warning-hover: hsl(var(--hue-warning), 90%, 45%);

    --error: hsl(var(--hue-error), 85%, 50%);
    --error-hover: hsl(var(--hue-error), 85%, 40%);

    /* Background & Surface Colors */
    --bg: hsl(var(--hue-primary), 20%, 97%);
    --bg-hover: hsl(var(--hue-primary), 20%, 93%);
    --bg-secondary: hsl(var(--hue-primary), 15%, 92%);
    --bg-card: hsl(0, 0%, 100%);

    /* Text Colors */
    /* Text Colors */
    --text-primary: #000000;
    --text-secondary: #222222;
    --text-tertiary: #444444;

    /* Border Colors */
    --border: hsl(var(--hue-neutral), 15%, 85%);
    --border-hover: hsl(var(--hue-neutral), 15%, 70%);

    /* Focus/Selection */
    --focus-ring: hsla(var(--hue-primary), 90%, 50%, 0.35);
    --selection-bg: hsla(var(--hue-primary), 90%, 65%, 0.3);

    /* Primary variations with opacity */
    --primary-bg-subtle: hsla(var(--hue-primary), 85%, 65%, 0.1);
    --primary-bg-light: hsla(var(--hue-primary), 85%, 65%, 0.15);
    --primary-bg-medium: hsla(var(--hue-primary), 85%, 65%, 0.3);
    --primary-border-light: hsla(var(--hue-primary), 85%, 65%, 0.3);

    /* Additional color variations with opacity */
    --success-bg-subtle: hsla(var(--hue-success), 65%, 45%, 0.1);
    --accent-bg-subtle: hsla(var(--hue-accent), 85%, 50%, 0.1);
    --warning-bg-subtle: hsla(var(--hue-warning), 90%, 55%, 0.1);
    --secondary-bg-subtle: hsla(var(--hue-secondary), 65%, 50%, 0.1);

    /* Overlay colors */
    --overlay-bg: rgba(0, 0, 0, 0.4);

    /* Scrollbar - Light Mode */
    --scrollbar-thumb-color: hsla(var(--hue-primary), 85%, 65%, 0.4);
    --scrollbar-thumb-hover-color: hsla(var(--hue-primary), 85%, 45%, 0.7);
}

/* Responsive utilities */
@media (max-width: 768px) {
    :root {
        /* Sidebar width for mobile - Drawer style */
        --sidebar-width: 80vw;
    }
}
`;

